import { BreakpointObserver } from '@angular/cdk/layout';
import { isPlatformServer, Location } from '@angular/common';
import type { AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, DOCUMENT, inject, PLATFORM_ID, Renderer2, signal } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { BreadcrumbsComponent } from 'components/atoms/breadcrumbs/breadcrumbs.component';
import type { IBreadcrumbItem } from 'components/atoms/breadcrumbs/entities/types';
import { ScrollUpButtonComponent } from 'components/atoms/scroll-up-button/scroll-up-button.component';
import { AuthorBioComponent } from 'components/molecules/author-description/author-bio.component';
import { PaginatorComponent } from 'components/molecules/paginator/paginator.component';
import { SharingButtonsComponent } from 'components/molecules/sharing-buttons/sharing-buttons.component';
import { HeaderViewService } from 'components/organisms/header-v2/service/header-view.service';
import { SidebarComponent } from 'components/organisms/sidebar/sidebar.component';
import type { ISidebarPageWidget } from 'components/organisms/widgets-loader/entities/types';
import { WidgetsLoaderComponent } from 'components/organisms/widgets-loader/partial/widgets-loader/widgets-loader.component';
import { StickyDirective } from 'src/directives/sticky/sticky.directive';
import type { ILandingPage, ILandingPageWidget } from 'src/pages/layouts/entities/types';
import { LayoutService } from 'src/pages/layouts/services/layout.service';
import { StringFormatPipe } from 'src/pipes/string-format/string-format.pipe';
import { AnalyticsService } from 'src/services/analytics/analytics.service';
import { EnvironmentService } from 'src/services/environment/environment.service';
import { ExitIntentPopupService } from 'src/services/exit-intent-popup/exit-intent-popup.service';
import { JsonLdService } from 'src/services/json-ld/json-ld.service';
import { PageDataService } from 'src/services/page-data/page-data.service';
import { PageLocalizationService } from 'src/services/page-localization/page-localization.service';
import { SocialMetaService } from 'src/services/social-meta/social-meta.service';
import { TwitterCardType } from 'src/services/social-meta/twitter.types';
import { PageLoadedCustomEventTrackingService } from 'src/services/tracking/implementations/page-loaded-custom-event-tracking/page-loaded-custom-event-tracking.service';
import { UIPreferencesService } from 'src/services/ui-preferences/ui-preferences.service';
import { TABLET_BREAKPOINT } from 'src/shared/constants/breakpoints/breakpoints';
import { applyLayout } from 'src/shared/helpers/apply-layout/apply-layout';
import { clearUrl } from 'src/shared/helpers/clear-url/clear-url';
import type { IArticleData } from 'src/shared/types/article';
import type { IRating } from 'src/shared/types/rating';
import { PRODUCT_TYPE } from 'src/tokens/product-type/product-type';

import { LayoutComponent } from '../../layouts/layout.component';
import { ErrorComponent } from '../error/error.component';

@Component({
    selector: 'tp-page-view',
    templateUrl: './page-view.component.html',
    styleUrls: ['./page-view.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ErrorComponent,
        WidgetsLoaderComponent,
        BreadcrumbsComponent,
        ScrollUpButtonComponent,
        SidebarComponent,
        SharingButtonsComponent,
        PaginatorComponent,
        AuthorBioComponent,
        StickyDirective,
    ],
})
export class PageViewComponent implements OnInit, OnDestroy, AfterViewInit {
    private readonly document = inject(DOCUMENT);
    private readonly platformId = inject(PLATFORM_ID);
    private readonly headerViewService = inject(HeaderViewService);
    private readonly layoutService = inject(LayoutService);
    private readonly layoutComponent = inject(LayoutComponent);
    private readonly stringFormatPipe = inject(StringFormatPipe);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly location = inject(Location);
    private readonly router = inject(Router);
    private readonly meta = inject(Meta);
    private readonly socialMetaService = inject(SocialMetaService);
    private readonly environmentService = inject(EnvironmentService);
    private readonly pageLocalizationService = inject(PageLocalizationService);
    private readonly pageLoadedCustomEventTrackingService = inject(PageLoadedCustomEventTrackingService);
    private readonly analyticsService = inject(AnalyticsService);
    private readonly exitIntentPopupService = inject(ExitIntentPopupService);
    private readonly cookieService = inject(CookieService);
    readonly uiPreferences = inject(UIPreferencesService);
    readonly pageDataService = inject(PageDataService);
    private renderer = inject(Renderer2);
    private readonly productType = inject(PRODUCT_TYPE);
    private readonly jsonLdService = inject(JsonLdService);
    private readonly breakpointObserver = inject(BreakpointObserver);

    public is404 = false;
    public viewData: ILandingPage;
    private readonly onDestroy$$ = new Subject<void>();
    public widgetsSidebar: ISidebarPageWidget | undefined;
    private readonly GA_COOKIE_NAME = '_ga';
    public breadcrumbs: IBreadcrumbItem[] | undefined;
    public readonly isMobile = signal(false);

    public ngOnInit(): void {
        this.activatedRoute.data
            .pipe(
                takeUntil(this.onDestroy$$),
                tap((data) => (this.is404 = !data?.view))
            )
            .subscribe((data) => this.processPageResponse(data?.view));

        this.breakpointObserver
            .observe([TABLET_BREAKPOINT])
            .pipe(takeUntil(this.onDestroy$$))
            .subscribe((result) => {
                this.isMobile.set(!result.matches);
            });
    }

    public ngAfterViewInit(): void {
        const gaCookie = this.cookieService.get(this.GA_COOKIE_NAME);

        gaCookie && this.pageLoadedCustomEventTrackingService.qualifiedIdentify(gaCookie);
    }

    public ngOnDestroy(): void {
        this.onDestroy$$.next(null);
        this.onDestroy$$.complete();
    }

    getCurrentPath(): string {
        const path = this.location.prepareExternalUrl(this.router.url);
        return clearUrl(path);
    }

    private processPageResponse(viewData: ILandingPage): void {
        if (viewData) {
            applyLayout(
                this.headerViewService,
                this.layoutService,
                this.layoutComponent,
                viewData,
                this.document,
                this.productType
            );
            this.updateMeta(viewData);
            this.updateMarker(viewData);
            this.handleExitIntentPopup(viewData);
            this.viewData = viewData;
            this.pageDataService.setData(this.viewData);
            this.pageLocalizationService.setPageLocalization(viewData);

            if (this.isSidebarLayout()) {
                this.widgetsSidebar = this.normalizeSidebarWidgets(viewData.widgets);
                this.updateBreadcrumbsFromApi(viewData);
            } else {
                this.widgetsSidebar = undefined;
                this.breadcrumbs = undefined;
            }

            // adding schema ld part
            if (this.isSidebarLayout()) {
                this.addArticleSchemaLd(viewData);
            } else if (this.shouldAddRatingSchema(viewData?.widgets as ILandingPageWidget[])) {
                this.addRatingSchemaLd(viewData?.jsonLD);
            }

            if (viewData?.jsonLD?.guidesPage?.howToSchema) {
                this.addHowToSchemaLd(viewData.jsonLD);
            }

            this.pageLoadedCustomEventTrackingService.track({
                origin: this.environmentService.environment.origin,
                pathname: this.getCurrentPath(),
            });

            if (this.analyticsService.getMarkers().length) {
                this.pageLoadedCustomEventTrackingService.munchkinTrack(
                    this.getCurrentPath(),
                    this.analyticsService.getMarkers()
                );
            }
        }
    }

    private updateMeta(viewData: ILandingPage): void {
        const commonMeta = viewData.seoMeta || viewData.seo_meta;
        const openGraphMeta = viewData?.social?.facebook;
        const twitterMeta = viewData?.social?.twitter;
        const hideInSearchMeta = [];
        const absoluteURL = this.environmentService.environment.origin + this.getCurrentPath();

        this.socialMetaService.setCommonMeta({
            title: commonMeta.title || '',
            description: commonMeta.description || '',
        });

        this.socialMetaService.setOpenGraphMeta({
            title: openGraphMeta?.title || commonMeta.title,
            description: openGraphMeta?.description || commonMeta.description,
            image: openGraphMeta?.image || commonMeta.sharedImage,
            url: absoluteURL,
        });

        const company = `@${this.jsonLdService.organization.name}`; // @Wrike, @Klaxoon

        this.socialMetaService.setTwitterMeta({
            title: twitterMeta?.title || commonMeta.title,
            description: twitterMeta?.description || commonMeta.description,
            image: twitterMeta?.image || commonMeta.sharedImage,
            card: TwitterCardType.SummaryLargeImage,
            site: company,
            creator: company,
        });

        if (commonMeta.noindex || commonMeta.nofollow) {
            if (commonMeta.noindex) {
                hideInSearchMeta.push('noindex');
            }

            if (commonMeta.nofollow) {
                hideInSearchMeta.push('nofollow');
            }

            this.meta.addTag({ name: 'robots', content: hideInSearchMeta.join(', ') });
        }
    }

    private updateMarker(viewData: ILandingPage): void {
        this.analyticsService.setMarkers(viewData.markers);
    }

    public get isSidebarBeforeEnabled(): boolean {
        return !!this.widgetsSidebar?.before_sidebar?.length;
    }

    public get isSidebarEnabled(): boolean {
        return !!this.widgetsSidebar?.sidebar?.length;
    }

    public get isSidebarAfterEnabled(): boolean {
        return !!this.widgetsSidebar?.after_sidebar?.length;
    }

    public get showAuthorsSection(): boolean {
        const introSidebarWidgetExist = this.widgetsSidebar?.sidebar?.find(
            (widget) => widget.class_name === 'intro_sidebar_widget'
        );

        return !!introSidebarWidgetExist && !!this.viewData?.authors?.length;
    }

    private handleExitIntentPopup(viewData: ILandingPage): void {
        if (viewData?.layout?.exit_popup) {
            this.exitIntentPopupService.init({
                data: viewData.layout.exit_popup,
                onDestroy$: this.onDestroy$$.asObservable(),
            });
        }
    }

    public isSidebarLayout(): boolean {
        return this.viewData?.layout?.name === 'sidebar';
    }

    private updateBreadcrumbsFromApi(viewData: ILandingPage): void {
        const breadcrumbsFromApi = Array.isArray(viewData?.breadcrumbs) ? viewData.breadcrumbs : [];

        const mappedBreadcrumbs: IBreadcrumbItem[] = breadcrumbsFromApi.map(({ title, url }, index) => ({
            text: index === 0 && url === '/' ? title || 'Home' : title, // Fallback to 'Home' if title is missing for the first breadcrumb
            link: url,
        }));

        if (mappedBreadcrumbs.length > 0) {
            const lastIndex = mappedBreadcrumbs.length - 1;
            mappedBreadcrumbs[lastIndex] = {
                ...mappedBreadcrumbs[lastIndex],
                forJsonLd: true,
            };
        }

        this.breadcrumbs = mappedBreadcrumbs.filter(
            ({ text, link }, index) => Boolean(link) && (index === 0 || Boolean(text))
        );
    }

    private addArticleSchemaLd(viewData: ILandingPage): void {
        if (isPlatformServer(this.platformId)) {
            const author = viewData.authors?.map((author) => ({
                '@type': 'Person',
                name: author?.name,
                jobTitle: author?.jobTitle,
                description: author?.description,
                image: {
                    '@type': 'ImageObject',
                    url: author?.avatarSrc,
                    width: '96',
                    height: '96',
                    caption: author.name,
                },
                sameAs: Object.values(author.socialLinks).filter(Boolean),
                url: `${this.environmentService.environment.origin + this.getCurrentPath()}#${this.stringFormatPipe.transform(author.name, 'kebab')}`,
            }));

            const seoMeta = viewData.seoMeta || viewData.seo_meta;
            const image = seoMeta?.sharedImage;

            const data: IArticleData = {
                image,
                author,
                datePublished: viewData?.published,
                dateModified: viewData?.lastUpdated,
                headline: seoMeta?.title,
                mainEntityOfPage: seoMeta?.canonical,
                description: seoMeta?.description,
            };

            this.appendJsonLdScriptToHead(this.jsonLdService.generateArticle(data));
        }
    }

    private addHowToSchemaLd(jsonLdData: ILandingPage['jsonLD']): void {
        if (isPlatformServer(this.platformId)) {
            const schema = jsonLdData?.guidesPage?.howToSchema;
            this.appendJsonLdScriptToHead(schema);
        }
    }

    private addRatingSchemaLd(jsonLdData: ILandingPage['jsonLD']): void {
        if (isPlatformServer(this.platformId)) {
            const { ratingValue, ratingCount, bestRating, worstRating, description } = jsonLdData;

            const rating: IRating = {
                ratingCount,
                ratingValue,
                bestRating,
                worstRating,
                description,
                image: this.headerViewService.logoLight || this.headerViewService.logoDark,
            };

            this.appendJsonLdScriptToHead(this.jsonLdService.generateAggregateRating(rating));
        }
    }

    private appendJsonLdScriptToHead<T>(schema: T): void {
        if (schema) {
            const script = this.renderer.createElement('script');
            script.type = 'application/ld+json';
            script.text = JSON.stringify(schema);
            this.renderer.appendChild(this.document.head, script);
        }
    }

    private shouldAddRatingSchema(widgets: ILandingPageWidget[]): boolean {
        return widgets.some((widget) => widget['hasRating'] === true);
    }

    private normalizeSidebarWidgets(widgets: ILandingPage['widgets']): ISidebarPageWidget {
        if (Array.isArray(widgets)) {
            return {
                before_sidebar: widgets.filter((widget) => widget.editor_name === 'before_sidebar') || [],
                sidebar: widgets.filter((widget) => widget.editor_name === 'sidebar') || [],
                after_sidebar: widgets.filter((widget) => widget.editor_name === 'after_sidebar') || [],
            };
        }

        return widgets as ISidebarPageWidget;
    }
}
